import type { NextRouter } from "next/router";
import { formatWithValidation } from "next/dist/shared/lib/router/utils/format-url";
import { getAssetTokenQuery, getDeploymentId } from "next/dist/shared/lib/deployment-id";
import { encodeURIPath } from "next/dist/shared/lib/encode-uri-path";

/**
 * Preloads a getServerSideProps page's `_next/data` JSON into the pages router's own data cache, so
 * a later client-side navigation to that page takes its props from memory (ortex/ortex-gui#1350).
 *
 * Next has no public way to do this: `router.prefetch` loads a page's code, and for a
 * getServerSideProps page its data request is a middleware "prefetch" that the server answers with
 * `x-middleware-skip` and no props. A navigation fetches the props through `fetchNextData`, which
 * first looks in `router.sdc` under the absolute data URL and drops the entry once it has used it
 * for an SSP page. So we put a real answer there, built the way `fetchNextData` builds its own.
 *
 * Written against next 16.4 (`dist/shared/lib/router/router.js`). It only ever adds an entry that the
 * router would otherwise fetch itself; if the internals it relies on are missing it does nothing,
 * and a navigation simply fetches as before. Entries the reader does not use expire after
 * `NEXT_DATA_TTL_MS`, so props are never older than that when a navigation takes them.
 */

export const NEXT_DATA_TTL_MS = 5 * 60 * 1000;

/**
 * Prefetches a page's code (its scripts and styles from the build manifest) without running it.
 *
 * Not `router.prefetch`: that also asks for the page's data as a middleware prefetch (answered with
 * no props for a getServerSideProps page) and then, in an idle callback, loads and RUNS every chunk
 * of the page. For the short-interest tab that was one main-thread task of ~1 s under 4x CPU
 * throttling, long enough to delay a tap on the overview (INP). Here the files only go into the HTTP
 * cache, through the same `<link rel="prefetch">` tags Next uses, so its own prefetch on hover or a
 * navigation finds them there. Resolves false when the manifest is not available.
 */
export const prefetchPageFiles = (route: string): boolean => {
    try {
        const manifest = (window as unknown as { __BUILD_MANIFEST?: Record<string, unknown> }).__BUILD_MANIFEST;
        const files = manifest?.[route];
        if (!Array.isArray(files) || !files.length) return false;
        const assetPrefix = (window as unknown as { __NEXT_DATA__?: { assetPrefix?: string } }).__NEXT_DATA__?.assetPrefix ?? "";
        for (const file of files) {
            if (typeof file !== "string") continue;
            // Built exactly as Next's route loader builds them (path encoding and the deployment-id
            // query), so a later load of the same file hits the HTTP cache.
            const href = `${assetPrefix}/_next/${encodeURIPath(file)}${getAssetTokenQuery()}`;
            if (document.querySelector(`link[rel="prefetch"][href^="${href}"], script[src^="${href}"], link[rel="stylesheet"][href^="${href}"]`)) continue;
            const link = document.createElement("link");
            link.rel = "prefetch";
            link.as = file.endsWith(".css") ? "style" : "script";
            link.href = href;
            document.head.appendChild(link);
        }
        return true;
    } catch {
        return false;
    }
};

interface RouterInternals {
    sdc?: Record<string, Promise<unknown>>;
    isPreview?: boolean;
    pageLoader?: { getDataHref?: (params: { href: string; asPath: string; skipInterpolation?: boolean }) => string };
}

/**
 * The router instance itself. `useRouter()` returns a public copy of it (`makePublicRouterInstance`)
 * without the data cache or the page loader; the pages router keeps the real one on `window.next`.
 */
const routerInternals = (router: NextRouter): RouterInternals => ((typeof window !== "undefined" && (window as unknown as { next?: { router?: unknown } }).next?.router) || router) as RouterInternals;

/** The data URL a navigation to `asPath` (page `route`, dynamic params `query`) will request. */
export const nextDataHref = (router: NextRouter, route: string, query: Record<string, string>, asPath: string): string | null => {
    const internals = routerInternals(router);
    const getDataHref = internals.pageLoader?.getDataHref;
    if (typeof getDataHref !== "function") return null;
    return getDataHref.call(internals.pageLoader, { href: formatWithValidation({ pathname: route, query }), asPath, skipInterpolation: true });
};

/** True when the router's data cache holds an entry for a navigation to `asPath` (one not yet used). Never throws. */
export const hasPreloadedNextData = (router: NextRouter, route: string, query: Record<string, string>, asPath: string): boolean => {
    try {
        const internals = routerInternals(router);
        const dataHref = internals.sdc && nextDataHref(router, route, query, asPath);
        return !!dataHref && new URL(dataHref, window.location.href).href in internals.sdc;
    } catch {
        return false;
    }
};

/** Options for `preloadNextData`. */
export interface PreloadNextDataOptions {
    /**
     * Fetch priority hint (`fetch(..., { priority })`). A speculative preload passes "low" so it
     * never competes with requests the page needs now. Browsers without the hint ignore it.
     */
    priority?: "high" | "low" | "auto";
}

/**
 * Fetches the page's props and stores them where the next navigation to `asPath` looks first.
 * Resolves true when an entry was stored. Never throws.
 */
export const preloadNextData = async (router: NextRouter, route: string, query: Record<string, string>, asPath: string, options: PreloadNextDataOptions = {}): Promise<boolean> => {
    try {
        const internals = routerInternals(router);
        if (!internals.sdc || internals.isPreview) return false;
        const dataHref = nextDataHref(router, route, query, asPath);
        if (!dataHref) return false;
        const cacheKey = new URL(dataHref, window.location.href).href;

        const deploymentId = getDeploymentId();
        const init: RequestInit & { priority?: PreloadNextDataOptions["priority"] } = { credentials: "same-origin", headers: deploymentId ? { "x-deployment-id": deploymentId } : {} };
        if (options.priority) init.priority = options.priority;
        const response = await fetch(dataHref, init);
        if (!response.ok) return false;
        const text = await response.text();
        let json: unknown;
        try {
            json = JSON.parse(text);
        } catch {
            return false;
        }
        // A redirect (`__N_REDIRECT`) or not-found answer is left for the navigation to fetch and handle.
        const pageProps = (json as { pageProps?: Record<string, unknown> })?.pageProps;
        if (!pageProps || "__N_REDIRECT" in pageProps) return false;

        const entry = Promise.resolve({ dataHref, json, response, text, cacheKey });
        internals.sdc[cacheKey] = entry;
        setTimeout(() => {
            if (internals.sdc?.[cacheKey] === entry) delete internals.sdc[cacheKey];
        }, NEXT_DATA_TTL_MS);
        return true;
    } catch {
        return false;
    }
};
